/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { dedupeBy } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Flag, Briefcase, HandHeart } from 'lucide-react';
import { EmptyState } from '@/components/UIExtras';
import { motion } from 'framer-motion';
import { CreateBoardItemDialog } from '@/components/bacheca/CreateBoardItemDialog';
import { BoardItemCard } from '@/components/bacheca/BoardItemCard';
import { boardItemVisualKey } from '@/components/bacheca/typeMeta';

// typeTab: 'all' | 'initiative' | 'help_project' | 'job'
const TYPE_TAB_PARAM = { initiative: 'initiative', help_project: 'help,project', job: 'job' };
const TYPE_TAB_CREATE_DEFAULT = { help_project: 'help', job: 'job' };
const PAID_FILTERS = ['all', 'free', 'paid'];

const buildParams = (typeTab, filter) => {
  const params = {};
  if (filter === 'free') params.is_paid = false;
  if (filter === 'paid') params.is_paid = true;
  if (TYPE_TAB_PARAM[typeTab]) params.type = TYPE_TAB_PARAM[typeTab];
  return params;
};

const BachecaPage = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeTab, setTypeTab] = useState('all');
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/initiatives', { params: buildParams(typeTab, filter) });
      // Rete di sicurezza: mai due annunci identici (stesso id, o stesso autore + titolo + tipo)
      setItems(dedupeBy(dedupeBy(res.data), boardItemVisualKey));
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [typeTab, filter]);

  useEffect(() => { load(); }, [load]);

  const onCreated = (x) => setItems(prev => [x, ...prev]);
  const onChanged = (u) => setItems(prev => prev.map(x => x.id === u.id ? u : x));
  const onDeleted = (id) => setItems(prev => prev.filter(x => x.id !== id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-display text-2xl font-semibold tracking-tight">{t('bacheca')}</h1>
        <CreateBoardItemDialog onCreated={onCreated} defaultType={TYPE_TAB_CREATE_DEFAULT[typeTab] || 'initiative'} />
      </div>
      <Tabs value={typeTab} onValueChange={setTypeTab}>
        <TabsList className="w-full flex flex-wrap justify-start gap-1 bg-secondary/60 p-1 rounded-full h-auto">
          <TabsTrigger value="all" className="rounded-full text-xs" data-testid="bacheca-tab-all">{t('all')}</TabsTrigger>
          <TabsTrigger value="initiative" className="rounded-full text-xs gap-1" data-testid="bacheca-tab-initiative">
            <Flag className="h-3.5 w-3.5" /> {t('initiatives')}
          </TabsTrigger>
          <TabsTrigger value="help_project" className="rounded-full text-xs gap-1" data-testid="bacheca-tab-help_project">
            <HandHeart className="h-3.5 w-3.5" /> {t('helpsProjects')}
          </TabsTrigger>
          <TabsTrigger value="job" className="rounded-full text-xs gap-1" data-testid="bacheca-tab-job">
            <Briefcase className="h-3.5 w-3.5" /> {t('jobs')}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex gap-1 flex-wrap">
        {PAID_FILTERS.map(k => (
          <button key={k} onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${filter === k ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'}`} data-testid={`bacheca-filter-${k}`}>
            {k === 'all' ? t('all') : t(k)}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading && <div className="text-sm text-muted-foreground" data-testid="bacheca-loading">{t('loading')}</div>}
        {!loading && items.length === 0 && (
          <div className="md:col-span-2"><EmptyState icon={<Flag className="h-6 w-6" />} title={t('noInitiatives')} subtitle={t('beFirstToPost')} /></div>
        )}
        {items.map((i) => (
          <motion.div key={i.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
            <BoardItemCard item={i} onChanged={onChanged} onDeleted={onDeleted} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default BachecaPage;
